import { useEffect } from "react";

/**
 * Fallback selector reporter for Lovable Visual Edit.
 *
 * The Lovable editor iframe bridge sends TOGGLE_SELECTOR / SET_CUSTOM_CURSOR
 * messages and expects an in-iframe reporter script to answer with the
 * currently hovered / selected DOM node so the parent overlay can highlight
 * it and map it back to source. When that injected reporter is missing
 * (observed for TanStack Start previews at *.lovableproject.com), the
 * editor stays stuck and every UPDATE_SELECTED_ELEMENTS payload from parent
 * is empty.
 *
 * This component fills that gap client-side by:
 *  - listening for TOGGLE_SELECTOR / SET_CUSTOM_CURSOR from lovable.* origins,
 *  - tracking pointer hover + click while the selector is active,
 *  - resolving the nearest element carrying a `data-tsd-source` attribute
 *    (emitted by the Lovable component tagger) or falling back to the raw
 *    target,
 *  - posting a normalized element descriptor back to the parent under
 *    several message-type aliases the editor is known to accept
 *    (UPDATE_SELECTED_ELEMENTS, ELEMENT_HOVERED, ELEMENT_CLICKED,
 *    SELECTOR_ELEMENT_SELECTED). Extra aliases are harmless if unused.
 *
 * The bridge is passive when the selector is off and never intercepts
 * events for normal users (it only attaches capture listeners once the
 * editor asks it to).
 */

type SourceLocation = {
  file: string;
  line: number;
  column: number;
};

type ElementDescriptor = {
  id: string;
  source: string | null;
  sourceLocation: SourceLocation | null;
  tagName: string;
  className: string;
  textContent: string;
  rect: {
    x: number;
    y: number;
    width: number;
    height: number;
    top: number;
    left: number;
    right: number;
    bottom: number;
  };
  attributes: Record<string, string>;
  outerHTMLSnippet: string;
};

function isLovableOrigin(origin: string): boolean {
  if (!origin) return false;
  try {
    const { hostname } = new URL(origin);
    return (
      hostname === "lovable.app" ||
      hostname.endsWith(".lovable.app") ||
      hostname === "lovable.dev" ||
      hostname.endsWith(".lovable.dev") ||
      hostname === "lovableproject.com" ||
      hostname.endsWith(".lovableproject.com")
    );
  } catch {
    return false;
  }
}

function parseTsdSource(value: string | null): SourceLocation | null {
  if (!value) return null;
  // Format written by the tagger: "/absolute/or/project/path.tsx:LINE:COL"
  const match = value.match(/^(.*):(\d+):(\d+)$/);
  if (!match) return null;
  return {
    file: match[1],
    line: Number(match[2]),
    column: Number(match[3]),
  };
}

function findTaggedAncestor(node: Element | null): Element | null {
  let cur: Element | null = node;
  while (cur && cur !== document.body) {
    if (cur.hasAttribute("data-tsd-source") || cur.hasAttribute("data-lov-id")) {
      return cur;
    }
    cur = cur.parentElement;
  }
  return node;
}

function describe(el: Element): ElementDescriptor {
  const rect = el.getBoundingClientRect();
  const source = el.getAttribute("data-tsd-source") || el.getAttribute("data-lov-id");
  const attributes: Record<string, string> = {};
  for (const attr of Array.from(el.attributes)) {
    attributes[attr.name] = attr.value;
  }
  const id =
    el.getAttribute("data-lov-id") ||
    el.getAttribute("data-tsd-source") ||
    `${el.tagName.toLowerCase()}#${(el as HTMLElement).id || ""}.${el.className || ""}`;
  return {
    id,
    source,
    sourceLocation: parseTsdSource(source),
    tagName: el.tagName.toLowerCase(),
    className: typeof el.className === "string" ? el.className : "",
    textContent: (el.textContent || "").trim().slice(0, 160),
    rect: {
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
      top: rect.top,
      left: rect.left,
      right: rect.right,
      bottom: rect.bottom,
    },
    attributes,
    outerHTMLSnippet: el.outerHTML.slice(0, 400),
  };
}

function postToParent(payload: Record<string, unknown>) {
  try {
    window.parent?.postMessage(payload, "*");
  } catch {
    // ignore
  }
}

function broadcast(kind: "hover" | "click", descriptor: ElementDescriptor) {
  // Fan out to several message shapes the editor is known to accept.
  const base = { source: "ve-selector-bridge", element: descriptor, elements: [descriptor] };
  if (kind === "hover") {
    postToParent({ type: "ELEMENT_HOVERED", payload: descriptor, ...base });
    postToParent({ type: "HOVER_ELEMENT", payload: descriptor, ...base });
    postToParent({ type: "UPDATE_HOVERED_ELEMENTS", payload: [descriptor], ...base });
  } else {
    postToParent({ type: "ELEMENT_CLICKED", payload: descriptor, ...base });
    postToParent({ type: "SELECTOR_ELEMENT_SELECTED", payload: descriptor, ...base });
    postToParent({ type: "UPDATE_SELECTED_ELEMENTS", payload: [descriptor], ...base });
  }
}

export function VisualEditSelectorBridge() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    let inIframe = false;
    try {
      inIframe = window.self !== window.top;
    } catch {
      inIframe = true;
    }
    if (!inIframe) return;

    let selectorActive = false;
    let cursorMode: string | null = null;
    let lastHoverId: string | null = null;

    const onMessage = (e: MessageEvent) => {
      if (!isLovableOrigin(e.origin)) return;
      const data = e.data as Record<string, unknown> | null;
      if (!data || typeof data !== "object") return;
      const type = typeof data.type === "string" ? data.type : "";
      const payload = (data.payload as Record<string, unknown> | undefined) || {};

      if (type === "TOGGLE_SELECTOR") {
        selectorActive = Boolean(payload.isActive);
      }
      if (type === "SET_CUSTOM_CURSOR") {
        cursorMode = typeof payload.cursor === "string" ? payload.cursor : null;
        // "selection", "text", "draw", etc. all activate hover reporting.
        if (cursorMode && cursorMode !== "default" && cursorMode !== "none") {
          selectorActive = true;
        }
        if (cursorMode === "default" || cursorMode === "none" || cursorMode === null) {
          // Editor cleared the cursor => selector likely off.
          if (!payload.cursor) selectorActive = false;
        }
      }
    };

    const shouldReport = () => selectorActive || cursorMode !== null;

    const resolveTarget = (ev: PointerEvent | MouseEvent): Element | null => {
      const raw = ev.target as Element | null;
      if (!raw) return null;
      return findTaggedAncestor(raw);
    };

    const onPointerMove = (ev: PointerEvent) => {
      if (!shouldReport()) return;
      const el = resolveTarget(ev);
      if (!el) return;
      const descriptor = describe(el);
      if (descriptor.id === lastHoverId) return;
      lastHoverId = descriptor.id;
      broadcast("hover", descriptor);
    };

    const onClick = (ev: MouseEvent) => {
      if (!shouldReport()) return;
      const el = resolveTarget(ev);
      if (!el) return;
      // Prevent the click from navigating / submitting while the editor's
      // selector is active.
      ev.preventDefault();
      ev.stopPropagation();
      broadcast("click", describe(el));
    };

    window.addEventListener("message", onMessage);
    window.addEventListener("pointermove", onPointerMove, true);
    window.addEventListener("click", onClick, true);

    // Announce readiness so the parent bridge knows an in-iframe reporter
    // is present. Harmless if the editor ignores it.
    postToParent({ type: "SELECTOR_BRIDGE_READY", source: "ve-selector-bridge" });

    return () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("pointermove", onPointerMove, true);
      window.removeEventListener("click", onClick, true);
    };
  }, []);

  return null;
}